import { Util } from '@/modules/util'
import { Request } from '@/classes/Request'

export interface FundingSourceDeleteRateLimitError {
  isDeleteRateLimit: true
  retryAfterSeconds: number
  resetAt: string | null
  failureReason: string
}

export interface FundingSourceAutoPayInUseError {
  isAutoPayInUse: true
  failureReason: string
}

// DELETE /ajax/funding_sources/{funding_source_id}
//
// When the funding source still backs the family user's auto payment settings the
// backend refuses with 409 FUNDING_SOURCE_IN_USE_FOR_AUTOPAY. Callers surface a
// warning and re-call with confirmAutoPayRemoval=true, which appends
// confirm_autopay_removal=1 so the backend clears it from those settings first.
export const fundingSource = async (
  fundingSourceId: number,
  confirmAutoPayRemoval = false
): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    // Explicitly type the Promise
    const req = new Request()
    const additionalParams = confirmAutoPayRemoval ? { confirm_autopay_removal: 1 } : undefined
    req
      .delete(`/ajax/funding_sources/${fundingSourceId}`, undefined, additionalParams)
      .then(response => {
        resolve()
      })
      .catch(err => {
        const status = err?.response?.status
        const data = err?.response?.data
        if (status === 429 && data?.error_code === 'TRANSIT_DELETE_RATE_LIMITED') {
          const rateLimitError: FundingSourceDeleteRateLimitError = {
            isDeleteRateLimit: true,
            retryAfterSeconds: Number(data.retry_after_seconds ?? 0),
            resetAt: data.reset_at ?? null,
            failureReason: data.failure_reason ?? ''
          }
          reject(rateLimitError)
          return
        }
        if (status === 409 && data?.error_code === 'FUNDING_SOURCE_IN_USE_FOR_AUTOPAY') {
          const autoPayError: FundingSourceAutoPayInUseError = {
            isAutoPayInUse: true,
            failureReason: data.failure_reason ?? ''
          }
          reject(autoPayError)
          return
        }
        reject(new Error('Failed to delete funding source: ' + err))
      })
  })
}
